import React, { FC, MouseEvent, useCallback, useEffect, useRef, useState } from 'react';
import { Button, IconBox, Input, Portal, Type as T } from "@infobis/ui-styles-utils";

import AuthLayout from "@components/layouts/authLayout/AuthLayout";

import backLogin from "@images/back/loginBack.png";

import st from "./authPage.module.scss";
import { isEmpty } from "lodash";
import { useActions } from "@hooks/useActions";
import { useTypedSelector } from "@hooks/useTypedSelector";
import { useNavigate } from "react-router-dom";
import logger from "@utils/logger";
import { Player } from '@lottiefiles/react-lottie-player';
import LoadingAnimation from '@assets/lottie/loading.json'
import OrganizationsTree from "@elements/OrganizationsTree/OrganizationsTree";
import { AuthenticateOrganizationActionType } from "@infobis/as-react-auth";
import { PortalComputedProps } from "@infobis/ui-styles-utils/components/Portal/Portal";
import Logo from "@elements/logo/Logo";
import { useEventListener, useUpdateEffect } from "usehooks-ts";
import { AsClientNode } from "@api/auth/OrganizationService";

export type TNode<T> = {
    isValid?: boolean
    id: string,
    name: string,
    data: TNode<T>[],
    childrenIds?: string[],
    isLeaf: boolean,
    parentIds?: string[],
    underlying: T
}

interface IAuthPageContainer {
    username: string,
    password: string,
    handleKeyPress: any,
    handleLoginClick: any,
    handlePasswordChange: any,
    handleUsernameChange: any,
}

export type Organization = (AsClientNode & { kind: "company" } | (AsClientNode & {
    companyId: string,
    kind: "department"
}))

const company2Node = (company: AsClientNode): TNode<Organization> => {
    return {
        id: String(company.glid),
        name: company.name,
        underlying: { ...company, kind: "company" },
        isLeaf: company.leaf,
        data: []
    }
}


const log = logger.getLogger("auth.page")

export const authenticateOrganization = async (organization: TNode<Organization>,
                                               organizations: AsClientNode[],
                                               authenticate: AuthenticateOrganizationActionType) => {
    if (organization.underlying?.kind === "department") {
        const organizationId = organization.underlying.companyId
        const foundOrganization = organizations.find(i => String(i.glid) === organizationId)
        if (foundOrganization) {
            return authenticate({
                // @ts-ignore
                organization: {
                    title: foundOrganization.name,
                    id: foundOrganization.glid,
                },
                // @ts-ignore
                department: {
                    glid: organization.underlying.glid,
                    name: organization.name
                }
            })
        } else {
            throw new Error("Organization is unexpectedly empty for id " + organizationId)
        }
    } else {
        return authenticate({
            // @ts-ignore
            organization: {
                title: organization.underlying.name,
                id: organization.underlying.glid
            },
        })
    }
}
const AuthPage: FC<{}> = (props) => {
    const navigate = useNavigate()
    const {
        removeToken,
        authenticateTokenAction,
        fetchOrganizationsTree,
        authenticateOrganizationAction,
        setAccessToken
    } = useActions();
    const [visibility, setVisibility] = useState<boolean>(false)
    const [departmentInputVisibility, setDepartmentInputVisibility] = useState<boolean>(false)
    const [serverInputVisibility, setServerInputVisibility] = useState<boolean>(false)

    const [username, setUsername] = useState<string>("");
    const [password, setPassword] = useState<string>("");
    const [serverId, setServerId] = useState<string | undefined>(undefined);
    const [isLoading, setLoading] = useState<boolean>(false);

    const onForgotPasswordClick = (e: MouseEvent<HTMLSpanElement>) => {
        e.preventDefault()
    }
    const onNextButtonClick = async () => {
        setLoading(true)
        // TODO! Why unwrap isn't defined on these async thunks
        // @ts-ignore
        authenticateTokenAction({ login: username, password, serverId }).unwrap()
            // @ts-ignore
            .then((tokenResponse) => {
                if (!tokenResponse.serverId) return Promise.resolve(setServerInputVisibility(true))
                // @ts-ignore
                else return fetchOrganizationsTree().unwrap().then(({ data }) => {
                    checkOrganization(data)
                })
            })
            .then(() => setLoading(false))
            .catch(() => setLoading(false))
    }

    const onLoginButtonClick = async () => {
        log.debug("On login button click...", organization)
        if (organization) {
            await proceedLoginBy(organization)
        }
    }

    const onKeyPress = useCallback(async (e: any) => {
        if (e.key === "Enter" && !isEmpty(username) && !isEmpty(password)) {
            return onNextButtonClick()
        }
    }, [username, password])

    const [organization, setOrganization] = useState<TNode<Organization> | undefined>();
    const { accessToken, apiError, isApiLoading } = useTypedSelector((state) => state.auth);
    const organizations: AsClientNode[] = useTypedSelector((state) => state.organizations.tree ? state.organizations.tree.data : []);
    const organizationsApiError = useTypedSelector((state) => state.organizations.apiError);
    const hasAuthenticationError = !isEmpty(apiError) || !isEmpty(organizationsApiError)

    const proceedLoginBy = useCallback(async (organizationNode: TNode<Organization>) => {
        setLoading(true)
        if(!accessToken?.serverId && serverId) {
            log.debug("Access token serverId is empty, set serverId from input", serverId)
            await setAccessToken({...accessToken!, serverId })
        }
        await authenticateOrganization(organizationNode, organizations, authenticateOrganizationAction)
        setLoading(false)
        navigate('/')
    }, [accessToken, serverId])

    useEffect(() => {
        if (accessToken) {
            log.debug("Authentication was successful", accessToken)
        } else {
            log.info("Oops, accessToken is empty, it looks like someone should logging in...")
            setDepartmentInputVisibility(false)
            setDepartmentsVisible(false)
        }
    }, [accessToken])

    useUpdateEffect(() => {
        log.debug("Username or password has been changed thus remove the access token...")
        removeToken()
    }, [username, password])

    const checkOrganization = useCallback((organizations: AsClientNode[]) => {
        if (!departmentInputVisibility && organizations.length > 0) {
            if (organizations.length === 1) {
                log.debug("It looks like there's one organization preset, thus proceed without the department choose step", organizations[0])
                proceedLoginBy(company2Node(organizations[0]!))
            } else setDepartmentInputVisibility(true)
        }
    }, [proceedLoginBy])

    const onOrganizationChoose = (node: TNode<Organization> | undefined) => {
        setOrganization(node)
        setDepartmentsVisible(false)
    }
    const [nextButtonDisabled, setNextButtonDisabled] = useState(true)
    useEffect(() =>
        setNextButtonDisabled(isEmpty(password) || isEmpty(username) || isLoading), [password, username, isLoading])
    const NextButton = () => <Button
        className={st.button}
        disabled={nextButtonDisabled}
        size={T.Size.LARGE}
        width={T.Width.FULL}
        onClick={onNextButtonClick}
    >
        {(isLoading) ? <Player
            className={st.loading_animation}
            src={LoadingAnimation}
            loop
            autoplay></Player> : "Далее"}
    </Button>

    const LoginButton = () => <Button
        className={st.button}
        disabled={organization === undefined || isLoading}
        size={T.Size.LARGE}
        width={T.Width.FULL}
        onClick={onLoginButtonClick}
    >
        {(isLoading) ? <Player
            className={st.loading_animation}
            src={LoadingAnimation}
            loop
            autoplay></Player> : "Войти"}
    </Button>

    const LoginInput = () => <Input.Text
        className={st.login}
        placeholder={"Логин"}
        label={"Логин"}
        disabled={isLoading}
        invalid={hasAuthenticationError}
        onChange={setUsername}
        value={username}
        width={T.Width.AUTO}
        onKeyPress={onKeyPress}
    />

    const ServerInput = () => <Input.Text
        placeholder={"Сервер"}
        label={"Сервер"}
        disabled={isLoading}
        onChange={setServerId}
        value={serverId}
        width={T.Width.AUTO}
        onKeyPress={onKeyPress}
    />

    const PasswordInput = () => <Input.Password
        className={st.password}
        onChange={setPassword}
        disabled={isLoading}
        value={password}
        label={"Пароль"}
        inputRef={passwordRef}
        markInvalid={hasAuthenticationError}
        type={visibility ? T.InputNameType.Text : T.InputNameType.PASSWORD}
        width={T.Width.AUTO}
        placeholder={"Пароль"}
        onKeyPress={onKeyPress}
        iconAfter={() => <IconBox
            className={st.password_visibility}
            onClick={(e: MouseEvent<any>) => {
                e.stopPropagation()
                setVisibility(!visibility)
            }}
            icon={visibility ? T.IconName.VisibilityOn : T.IconName.VisibilityOff}
        />}
    />

    const onDepartmentInputClick = (e: MouseEvent<any>) => {
        e.stopPropagation()
        setDepartmentsVisible(!isDepartmentsVisible)
    }

    const DepartmentInput = () => <Input.Text
        readOnly
        label={"Департамент"}
        value={organization?.name}
        disabled={isLoading}
        width={T.Width.AUTO}
        placeholder={"Выберите департамент"}
        onInputClick={onDepartmentInputClick}
        iconAfter={() => <IconBox
            onClick={onDepartmentInputClick}
            icon={T.IconName.ChevronRight}/>}
    />


    const passwordRef = useRef<HTMLInputElement>(null)
    const [isDepartmentsVisible, setDepartmentsVisible] = useState(false)

    useEventListener('animationstart', (e) => {
        if (e.animationName === st.onAutoFillStart) setNextButtonDisabled(false)
    }, passwordRef)

    return (
        <AuthLayout>
            <div className={st.content}>
                <img src={backLogin} alt={"back"} className={st.background}/>
                <div className={st.card}>
                    <div className={st.logo}>
                        <Logo
                            size={'l'}
                            color={"#474A5A"}
                            hoverColor={"#474A5A"}/>
                    </div>
                    <div className={st.title}>
                        Вход в систему
                    </div>
                    <div className={st.form}>
                        {LoginInput()}
                        {PasswordInput()}
                        {serverInputVisibility && ServerInput()}
                        {departmentInputVisibility && DepartmentInput()}
                        <div className={st.error}>{apiError || organizationsApiError}</div>
                        {departmentInputVisibility ? LoginButton() : NextButton()}
                        {
                            <Portal
                                container={document.getElementById("root")!}
                                targetRef={passwordRef}
                                trigger={isDepartmentsVisible}
                                position={'right'}
                                offsets={{ x: 77 }}
                                component={(props: PortalComputedProps<HTMLDivElement>) => <OrganizationsTree
                                    {...props}
                                    onChoose={onOrganizationChoose}
                                    onClose={() => setDepartmentsVisible(false)}
                                />}
                                onOutsideClick={() => setDepartmentsVisible(false)}/>
                        }
                    </div>

                </div>

            </div>
        </AuthLayout>
    )
}
export default AuthPage;